import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";
import AbtBgImg1 from "../../assets/img/website-images/few-words-about-akash-pharmacy.png";

/* ─── Variants ─────────────────────────────────────────────────── */
const fadeInLeft = {
  hidden: { opacity: 0, x: -70 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
  },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 70 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: "easeOut", delay },
  }),
};

const listContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.22, delayChildren: 0.35 } },
};

const listItemVariants = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

/* ─── Badge with GSAP count-up ─────────────────────────────────── */
function YearsBadge({ inView }) {
  const numRef = useRef(null);

  useEffect(() => {
    if (inView && numRef.current) {
      gsap.fromTo(
        numRef.current,
        { innerText: 0 },
        {
          innerText: 2,
          duration: 1.2,
          ease: "power2.out",
          snap: { innerText: 1 },
          onUpdate() {
            if (numRef.current) {
              numRef.current.innerText = Math.round(
                this.targets()[0].innerText,
              );
            }
          },
        },
      );
    }
  }, [inView]);

  return (
    <>
    {/* <motion.div
      className="about-text second-about three-about"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.5 }}
      transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1], delay: 0.55 }}
    >
      <span>
        <span ref={numRef}>7</span> <sub>+</sub>
      </span>
      <p>Years of Excellence</p>
    </motion.div> */}
    </>
  );
}

/* ─── Main Component ───────────────────────────────────────────── */
function University() {
  /* Left image column */
  const { ref: leftRef, inView: leftInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  /* Right content column */
  const { ref: rightRef, inView: rightInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  /* GSAP: floating decorative bg */
  const bgRef = useRef(null);
  useEffect(() => {
    if (bgRef.current) {
      gsap.to(bgRef.current, {
        y: -18,
        duration: 3.8,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    }
  }, []);

  return (
    <section className="about-area about-p pt-120 pb-120 p-relative fix">
      {/* Floating decorative bg */}
      <div className="animations-02" ref={bgRef}>
        <img src="/assets/img/bg/an-img-02.png" alt="contact-bg-an-01" />
      </div>

      <div className="container">
        <div className="row justify-content-center align-items-center">
          {/* ── Left: Image ── */}
          <div className="col-lg-6 col-md-12 col-sm-12">
            <motion.div
              ref={leftRef}
              className="s-about-img p-relative"
              variants={fadeInLeft}
              initial="hidden"
              animate={leftInView ? "visible" : "hidden"}
            >
              <motion.img
                src={AbtBgImg1}
                alt="img"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.4 }}
              />
              <YearsBadge inView={leftInView} />
            </motion.div>
          </div>

          {/* ── Right: Content ── */}
          <div className="col-lg-6 col-md-12 col-sm-12">
            <motion.div
              ref={rightRef}
              className="about-content s-about-content pl-15"
              variants={fadeInRight}
              initial="hidden"
              animate={rightInView ? "visible" : "hidden"}
            >
              {/* Title */}
              <div className="about-title second-title pb-25">
                <motion.h5
                  variants={fadeUp}
                  custom={0.1}
                  initial="hidden"
                  animate={rightInView ? "visible" : "hidden"}
                >
                  <i className="fal fa-graduation-cap" /> About Our Institute
                </motion.h5>
                <motion.h2
                  variants={fadeUp}
                  custom={0.2}
                  initial="hidden"
                  animate={rightInView ? "visible" : "hidden"}
                >
                  A Few Words About Akash Institute of Pharmacy
                </motion.h2>
              </div>

              {/* Paragraphs */}
              <motion.p
                className="txt-clr"
                variants={fadeUp}
                custom={0.3}
                initial="hidden"
                animate={rightInView ? "visible" : "hidden"}
              >
                Established in 2019 under the Akash Educational & Development Trust, Akash Institute of Pharmacy (AIP) is a premier centre for pharmaceutical education functioning under the Akash Group of Institutions. The institute is committed to nurturing competent, ethical, and innovative pharmacy professionals through quality education and practical training.
              </motion.p>

              <motion.p
                variants={fadeUp}
                custom={0.4}
                initial="hidden"
                animate={rightInView ? "visible" : "hidden"}
              >
                Affiliated to Rajiv Gandhi University of Health Sciences (RGUHS) and recognized by the Pharmacy Council of India (PCI), the institute offers a comprehensive Bachelor of Pharmacy (B.Pharm) program that combines strong academic learning with modern laboratory facilities, industry exposure, and research opportunities. Our goal is to prepare students to meet the evolving demands of the healthcare and pharmaceutical industries.
              </motion.p>

              {/* Vision & Mission — staggered list */}
              <div className="about-content2">
                <div className="row">
                  <div className="col-md-12">
                    <motion.ul
                      className="green2"
                      variants={listContainerVariants}
                      initial="hidden"
                      animate={rightInView ? "visible" : "hidden"}
                    >
                      <motion.li variants={listItemVariants}>
                        <div className="abcontent">
                          <div className="ano">
                            <span>01</span>
                          </div>
                          <div className="text">
                            <h3>Our Vision</h3>
                            <p>
                              To be a center of excellence in pharmaceutical education and research, nurturing competent, ethical, and innovative pharmacy professionals who contribute to healthcare advancement and societal well-being globally.
                            </p>
                          </div>
                        </div>
                      </motion.li>

                      <motion.li variants={listItemVariants}>
                        <div className="abcontent">
                          <div className="ano">
                            <span>02</span>
                          </div>
                          <div className="text">
                            <h3>Our Mission</h3>
                            <p>
                              To provide high-quality pharmacy education through modern teaching methodologies, well-equipped laboratories, and industry-relevant curriculum. We are committed to promote research, innovation, and entrepreneurship in pharmaceutical sciences and to instill ethical values, professional integrity, and social responsibility among students.
                            </p>
                          </div>
                        </div>
                      </motion.li>
                    </motion.ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default University;
